import { CalendarDots, CaretUpDown, UsersThree } from "@phosphor-icons/react";
import { type Dispatch, type SetStateAction, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import CampaignTypeIcon from "common/components/CampaignTypeIcon";
import DataTableV2 from "common/components/DataTableV2";
import Skeleton from "common/components/ui/Skeleton";
import { Tag } from "common/components/ui/Tag";
import { CAMPAIGN_TYPES } from "common/constants";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import { formatDate } from "common/helpers/utils";
import type { Campaign } from "common/types";

import { CAMPAIGN_OUTREACH_OPTIONS } from "../../../constants";
import {
  CampaignActionsDialog,
  CampaignActionsDropdown,
} from "../../CampaignActions";
import CampaignStateTag from "../../CampaignStateTag";
import WorkerAccountsTag from "../../WorkerAccountsTag";
import CampaignCompletion from "./CampaignCompletion";
import CampaignMetrics from "./CampaignMetrics";

import type { ColumnDef } from "@tanstack/react-table";
import type { CampaignActionType } from "../../../types";

interface DesktopTableProps {
  isLoading: boolean;
  campaigns: Campaign[];
  selectedCampaigns: Campaign[];
  setSelectedCampaigns: Dispatch<SetStateAction<Campaign[]>>;
}

export default function DesktopTable({
  isLoading,
  campaigns,
  selectedCampaigns,
  setSelectedCampaigns,
}: DesktopTableProps) {
  const navigate = useNavigate();
  const { permissions } = useSelectedWorkspaceContext();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign>(null);

  const [selectedActionType, setSelectedActionType] =
    useState<CampaignActionType>();

  function setAction(action: CampaignActionType, campaign: Campaign) {
    setSelectedCampaign(campaign);
    setIsDialogOpen(true);
    setSelectedActionType(action);
  }

  function onRowClick(campaign: Campaign) {
    navigate(campaign.id);
  }

  const columns = useMemo(() => {
    const campaignColumns: ColumnDef<Campaign>[] = [
      {
        id: "name",
        accessorKey: "name",
        header: ({ column }) => (
          <button
            type="button"
            className="flex items-center"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Name <CaretUpDown size={14} />
          </button>
        ),
        cell: ({
          row: {
            original: { type, outreach_type, name },
          },
        }) => {
          const { icon: SubtypeIcon } =
            CAMPAIGN_OUTREACH_OPTIONS[outreach_type] ?? {};

          const { label } = CAMPAIGN_TYPES[type] ?? {};

          return (
            <div className="flex items-center gap-2">
              {isLoading ? (
                <Skeleton className="size-10" />
              ) : (
                <CampaignTypeIcon
                  type={type}
                  isDrip={outreach_type === "drip"}
                />
              )}

              <div className="flex flex-col gap-y-1">
                {isLoading ? (
                  <Skeleton className="h-5 w-40" />
                ) : (
                  <h5 className="line-clamp-1 break-all text-md-bold">
                    {name}
                  </h5>
                )}

                <div className="flex items-center gap-1">
                  {isLoading ? (
                    <>
                      <Skeleton className="size-4" />
                      <Skeleton className="h-4 w-40" />
                    </>
                  ) : (
                    <>
                      <SubtypeIcon className="size-4 text-black-600" />
                      <span className="line-clamp-1 break-all text-button-xs text-black-500">
                        {label} •{" "}
                        {outreach_type === "drip" ? "1st" : "2nd & 3rd+"}{" "}
                        connections
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        },
        enableHiding: false,
      },
      {
        id: "owners",
        header: "Accounts",
        cell: ({
          row: {
            original: { owners, uses_connection_note },
          },
        }) => {
          if (isLoading) return <Skeleton className="h-5 w-32 rounded-full" />;

          return (
            <WorkerAccountsTag
              size="sm"
              owners={owners}
              isUsingNote={uses_connection_note}
            />
          );
        },
      },
      {
        id: "state",
        accessorKey: "state",
        header: "Status",
        cell: ({
          row: {
            original: {
              state,
              owners,
              start_date: startDate,
              uses_connection_note,
            },
          },
        }) => {
          if (isLoading) return <Skeleton className="h-5 w-16" />;

          return (
            <CampaignStateTag
              size="sm"
              state={state}
              owners={owners}
              startDate={startDate}
              isUsingNote={uses_connection_note}
            />
          );
        },
      },
      {
        id: "type",
        header: "Type",
        cell: ({
          row: {
            original: { outreach_type: outreachType },
          },
        }) =>
          isLoading ? (
            <Skeleton className="h-5 w-16" />
          ) : (
            <Tag size="sm" variant="tertiary">
              {outreachType === "drip" ? "Inbound" : "Outbound"}
            </Tag>
          ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created at",
        cell: ({
          row: {
            original: { created_at: createdAt },
          },
        }) =>
          isLoading ? (
            <Skeleton className="h-5 w-20" />
          ) : (
            <Tag
              size="sm"
              leftIcon={<CalendarDots weight="fill" />}
              variant="tertiary"
            >
              {formatDate(createdAt)}
            </Tag>
          ),
      },
      {
        id: "target_count",
        header: "Targets",
        cell: ({ row: { original } }) => {
          if (original.state === "INIT") {
            return;
          }

          if (isLoading) return <Skeleton className="h-5 w-14" />;

          return (
            <Tag
              size="sm"
              leftIcon={<UsersThree weight="fill" />}
              variant="tertiary"
            >
              {original.target_count}
            </Tag>
          );
        },
      },
      {
        id: "success",
        header: "Success",
        cell: ({ row: { original } }) => {
          if (original.state === "INIT" || original.state === "CREATED") {
            return;
          }

          if (isLoading) {
            return <Skeleton className="h-5 w-20" />;
          }

          return (
            <CampaignMetrics
              id={original.id}
              outreachType={original.outreach_type}
            />
          );
        },
      },
      {
        id: "completion",
        header: "Progress",
        cell: ({ row: { original } }) => {
          if (original.state === "INIT" || original.state === "CREATED") {
            return;
          }

          if (isLoading) {
            return (
              <div className="flex items-center gap-x-1">
                <Skeleton className="w-8 h-4" />
                <Skeleton className="h-2 w-24" />
              </div>
            );
          }

          return <CampaignCompletion id={original.id} />;
        },
      },
      {
        id: "actions",
        cell: ({ row: { original } }) => {
          if (isLoading)
            return <Skeleton className="inline-flex size-8 rounded-full" />;

          return (
            <CampaignActionsDropdown
              campaign={original}
              setAction={(action) => setAction(action, original)}
            />
          );
        },
        enableHiding: false,
      },
    ];

    return campaignColumns;
  }, [isLoading]);

  return (
    <>
      <DataTableV2
        id="campaigns"
        selectedIds={selectedCampaigns.map(({ id }) => id)}
        columns={columns}
        data={campaigns}
        onRowClick={onRowClick}
        isLoading={isLoading}
        onRowSelectionChange={
          permissions.includes("campaigns_full") && setSelectedCampaigns
        }
      />

      {!!selectedCampaign && (
        <CampaignActionsDialog
          isOpen={isDialogOpen}
          setIsOpen={setIsDialogOpen}
          actionType={selectedActionType}
          campaign={selectedCampaign}
        />
      )}
    </>
  );
}
